// src/components/SeriesModal.tsx
import React, { useMemo } from "react";
import { MRT_ColumnDef } from "material-react-table";
import {
  Checkbox,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useDetalleStore } from "../../hook/useDetalleStore.store";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useFetchGetAllSeries } from "@/shared/actions/erp/productofactorserie.actions";
import SampleTablePagination from "@/shared/components/ui/SampleTablePagination";
import { cellNotFilter, EstadoGestionInventarioENUM, OPCIONES_ESTADO_GESTION_INVENTARIO, useTableFilter, useTableServerSideFiltering } from "@/shared";
import { IDataSerie } from "@/shared/interfaces/erp/administration/productofactorserie.interface";
import CustomCantidadInput from "@/shared/components/ui/CustomCantidadInput";
import { toast } from "react-toastify";

export const CuadreModalSeries: React.FC = () => {
  const open = useDetalleStore((s) => s.seriesModalOpen);
  const ubicacion = useDetalleStore((s) => s.ubicacion);
  const producto = useDetalleStore((s) => s.producto);
  const closeSeriesModal = useDetalleStore((s) => s.closeSeriesModal);
  const saveSeriesToParent = useDetalleStore((s) => s.saveSeriesToParent);
  const seriesInteractions = useDetalleStore((s) => s.seriesInteractions);
  const upsertSeriesInteraction = useDetalleStore((s) => s.upsertSeriesInteraction);
  const dataProducto = useDetalleStore((s) => s.dataProducto);


  ///* fetch

  const {
    pagination,
    setPagination,
  } = useTableFilter();

  const {
    pageIndex,
    pageSize
  } = pagination

  const {
    filterObject,
    columnFilters,
    setColumnFilters
  } = useTableServerSideFiltering();

  const {
    data,
    isLoading,
    isRefetching
  } = useFetchGetAllSeries({
    enabled: open,
    params: {
      ubicacion,
      producto,
      page: pageIndex + 1,
      page_size: pageSize,
      ...filterObject,
    }
  })


  ///* react table
  const columns = useMemo<MRT_ColumnDef<IDataSerie>[]>(() => {
    return [
      {
        ...cellNotFilter,
        id: "check",
        header: "Acción",
        size: 60,
        Cell: ({ row }) => {
          const base = row.original;
          const patch = seriesInteractions[base.serie] ?? {};
          const checked = patch.check
            ?? (dataProducto?.metros_base == base.metros_actuales);

          return (
            <Checkbox
              checked={checked}
              onChange={(e) => upsertSeriesInteraction(base.serie,
                {
                  ...defaultValue(base),
                  check: e.target.checked
                })}
            />
          );
        },
      },
      {
        enableColumnFilter: true,
        accessorKey: "serie",
        header: "Serie",
        size: 180,
      },
      {
        ...cellNotFilter,
        accessorKey: "metros_actuales",
        header: "Stock actual",
        Cell: ({ row }) => dataProducto?.usa_metros
          ? `${row.original.metros_actuales} (METRO)`
          : "1"
      },
      {
        ...cellNotFilter,
        id: "descripcion",
        header: "Descripción",
        Cell: ({ row }) => {
          const base = row.original;
          const patch = seriesInteractions[base.serie] ?? {};
          const value = patch.observacion ?? "—";
          return (
            <TextField
              size="small"
              value={value}
              id={row.original.serie}
              variant="outlined"
              onChange={(e) => upsertSeriesInteraction(base.serie,
                {
                  ...defaultValue(base),
                  observacion: e.target.value
                })}
              fullWidth
            />
          );
        },
      },

      {
        accessorKey: 'tipo_gestion',
        header: 'Tipo Gestión',
        size: 200,
        // Renderizamos el componente Select directamente en la celda
        Cell: ({ row }) => {
          const base = row.original;

          return (
            <Select
              value={seriesInteractions[base.serie]?.tipo_gestion
                ?? EstadoGestionInventarioENUM.NO_REQUIERE}
              size="small"
              fullWidth
              onChange={(e: SelectChangeEvent) => {


                upsertSeriesInteraction(base.serie, {
                  ...defaultValue(base),
                  tipo_gestion: e.target.value,
                  // TODO: se recupera el id de serie cuando la gestion es diferente a NO GESTION, dato requerido para la comunicaicón con el microservicio
                  id_serie: base.id_series
                } as any)
              }

              }
            >
              {OPCIONES_ESTADO_GESTION_INVENTARIO.map((opcion) => (
                <MenuItem key={opcion} value={opcion}>
                  {opcion}
                </MenuItem>
              ))}
            </Select>
          );
        },
      },

      {
        ...cellNotFilter,
        id: 'cantidad_gestion',
        accessorKey: 'cantidad_gestion',
        header: 'Cantidad gestión',
        Cell: ({ row }) => {
          const base = row.original

          const valor = dataProducto?.usa_metros
            ? row.original.metros_actuales
            : 1

          return <CustomCantidadInput<any>
            value={seriesInteractions[base.serie]?.cantidad_gestion
              ?? 0}
            handleValue={(value) => {

              if (+value > valor) {
                toast.info('El valor no puede ser mayor a la cantidad en inventario')
                return
              }

              upsertSeriesInteraction(base.serie, {
                ...defaultValue(base),
                cantidad_gestion: value,
              } as any)

            }}
            row={row as any} //* TODO: validar
          />
        },
      },
    ];
  }, [seriesInteractions, upsertSeriesInteraction]);


  ///* funcion
  const onSave = () => {
    setColumnFilters([] as any)
    setPagination({
      pageIndex: 0,
      pageSize: 10
    })
    saveSeriesToParent();
  };

  const onClose = () => {
    setColumnFilters([] as any)
    closeSeriesModal()
    setPagination({
      pageIndex: 0,
      pageSize: 10
    })
  }

  const defaultValue = (data: IDataSerie) => {
    const {
      serie,
      id_series
    } = data;

    const cuadre = seriesInteractions?.[serie];

    return {
      id_series,
      serie,
      check: cuadre?.check
        ?? (dataProducto?.metros_base == data.metros_actuales),
      observacion: cuadre?.observacion ?? '—',
      cantidad_gestion: cuadre?.cantidad_gestion ?? 0,
      tipo_gestion: cuadre?.tipo_gestion ?? EstadoGestionInventarioENUM.NO_REQUIERE
    };
  };

  return (
    <ScrollableDialogProps
      minWidth="60%"
      /* isCloseIcon={false}
      onCloseIcon={() => {
        close()
      }} */
      open={open}
      title={`Revisar series para: ${producto}`}
      dialogContentText={'Series disponibles en la ubicación'}
      onConfirm={onSave}
      onReject={onClose}
      rejectTextBtn="Cancelar"
      showRejectBtn={true}
      confirmTextBtn="Guardar"
      confirmVariantBtn="outlined"
      contentNode={
        <>
          <SampleTablePagination<IDataSerie>
            columns={columns}
            data={data?.data.data ?? []}
            rowCount={data?.data?.count ?? 0}
            setPagination={setPagination}

            ///* filtro
            enableColumnFilters={true}
            enableColumnActions={true}
            enableManualFiltering={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}

            ///* paginacion
            pagination={pagination}
            rowNumber={true}
            isLoading={isLoading
              || isRefetching}
            muiTableBodyRowProps={({ row }: any) => {
              const r = row.original;

              const checked = seriesInteractions[r.serie]?.check
                ?? (dataProducto?.metros_base == r.metros_actuales);

              return {
                sx: checked
                  ? {
                    backgroundColor: "#789DBC"
                  }
                  : undefined,
              };
            }}
          />
        </>
      }
    />
  )
};
